import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";

import type { AdminTicketDetailsDto, AppTicketDto, TicketStatusDto, UserDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Gallery } from "@/components/Gallery/Gallery";
import type { IconComponent } from "@/components/Icon/Icon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { TicketCategoryDecisionPath } from "@/components/Ticket/TicketDetails/TicketCategoryDecisionPath";
import { TicketLeaveCommentModal } from "@/components/Ticket/TicketLeaveCommentModal";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { TicketStatusChangeModal } from "@/components/Ticket/TicketStatusChangeModal";
import { Video } from "@/components/Video/Video";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Link } from "@/design-system/Link";
import { RatingBadge } from "@/design-system/RatingBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { daysBetween } from "@/helpers/date";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { DetailField } from "@/product-patterns/tickets/DetailField";
import { changingAssigneeWillResultIn403, useTicketCommentHandler } from "@/queries/tickets/helpers";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

function getVisibilityOptionIcon(visibility: string): IconComponent {
  switch (visibility) {
    case "private":
      return <User01Icon size={16} />;
    case "sharedWithBuilding":
      return <Building01Icon size={16} />;
    case "sharedWithCommunity":
    default:
      return <Building04Icon size={16} />;
  }
}

interface TicketInfoProps {
  ticket: AdminTicketDetailsDto;
  showProject: boolean;
  showDecisionTreePath?: boolean;
}

type ModalState = { assignee?: UserDto } & (
  | { type: "none" }
  | { type: "confirmAssigneeWarning"; assignee: UserDto }
  | { type: "leaveAssigneeChangeComment"; assignee: UserDto }
  | { type: "leaveStatusChangeComment" }
  | { type: "changeStatusWithOptions" }
);

export function TicketInfo({ ticket, showProject, showDecisionTreePath = false }: TicketInfoProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [modalState, setModalState] = useState<ModalState>({ type: "none" });

  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const { createComment } = useTicketCommentHandler(ticket.id);
  const changeAssigneeMutation = ticketMutations.useUpdateAssignee();
  const changeStatusMutation = ticketMutations.useUpdateStatus();

  const initiateAssigneeChange = useCallback(
    (newAssignee: UserDto) => {
      if (newAssignee.id === ticket.assignee?.id) {
        return;
      }

      if (changingAssigneeWillResultIn403(ticket, newAssignee, sessionUser)) {
        setModalState({ type: "confirmAssigneeWarning", assignee: newAssignee });
      } else if (ticket.canAddInternalNote) {
        setModalState({ type: "leaveAssigneeChangeComment", assignee: newAssignee });
      } else {
        changeAssigneeMutation.mutate({
          ticketId: ticket.id,
          data: { assigneeId: newAssignee.id },
        });
      }
    },
    [ticket, sessionUser, changeAssigneeMutation],
  );

  const handleConfirmAssigneeWarning = async (confirmedAssignee?: UserDto) => {
    if (confirmedAssignee) {
      if (ticket.canAddInternalNote) {
        setModalState({ type: "leaveAssigneeChangeComment", assignee: confirmedAssignee });
      } else {
        await changeAssigneeMutation.mutateAsync({
          ticketId: ticket.id,
          data: { assigneeId: confirmedAssignee.id },
        });
        setModalState({ type: "none" });
      }
    } else {
      setModalState({ type: "none" });
    }
  };

  const handleAssigneeChangeWithOptionalNote = async (assignee?: UserDto, note?: string) => {
    if (assignee) {
      await changeAssigneeMutation.mutateAsync({
        ticketId: ticket.id,
        data: {
          assigneeId: assignee.id,
          note,
        },
      });
    }

    setModalState({ type: "none" });
  };

  const handleChangeStatus = async (newStatus: TicketStatusDto) => {
    if (newStatus.id === ticket.status.id) {
      return;
    }
    await changeStatusMutation.mutateAsync({
      ticketId: ticket.id,
      data: {
        statusId: newStatus.id,
        silent: false,
      },
    });

    if (newStatus.type === "inProgress" && ticket?.canAddInternalNote && ticket?.canAddPublicComment) {
      setModalState({ type: "leaveStatusChangeComment" });
    } else {
      setModalState({ type: "none" });
    }
  };

  const handleLeaveStatusChangeComment = async (value: string, isNote: boolean) => {
    await createComment({
      content: value,
      images: [],
      documents: [],
      videos: [],
      type: isNote ? "internal" : "public",
    });
    setModalState({ type: "none" });
  };

  const handleSubmitStatusChangeWithOptions = async (status: { id: string }) => {
    await changeStatusMutation.mutateAsync({
      ticketId: ticket.id,
      data: {
        statusId: status.id,
        silent: false,
      },
    });
    setModalState({ type: "none" });
  };

  const possibleStatusesForSelect =
    ticket?.possibleStatuses.map((status) => ({
      id: status.id,
      description: status.name,
      labelColor: status.color,
      defaultStatusId: status.type,
    })) || [];

  const displayedAssignee = modalState.assignee || ticket.assignee;

  return (
    <>
      <SectionCard>
        <Stack gap="md">
          <Inline gap="sm">
            <DetailField label={t("page.tickets.details.info.type")}>
              <Badge
                icon={getVisibilityOptionIcon(ticket.visibility)}
                variant={ticket.visibility === "private" ? "default" : "special"}
              >
                {ticket.visibility === "private"
                  ? t("page.tickets.details.info.type.private")
                  : t("page.tickets.details.info.type.collective")}
              </Badge>
            </DetailField>
            {showProject && (
              <DetailField label={t("page.tickets.details.info.project")}>
                <Link variant="body-emphasis" href={routes.tickets.list({ slug: sessionUser.project.slug })}>
                  {sessionUser.project.name}
                </Link>
              </DetailField>
            )}
            {showDecisionTreePath && ticket.decisionTreePath?.nodes.length ? (
              <DetailField label={t("page.tickets.details.info.category")}>
                <TicketCategoryDecisionPath categoryName={ticket.category.name} path={ticket.decisionTreePath} />
              </DetailField>
            ) : (
              <DetailField label={t("page.tickets.details.info.category")} value={ticket.category.name} />
            )}
            <DetailField
              label={t("page.tickets.details.info.open-since")}
              value={t("page.tickets.details.info.open-since.content", {
                count: daysBetween(ticket.createdAt, clockDate),
              })}
            />
            <DetailField label={t("page.tickets.details.info.id")} value={ticket.id} />
            <DetailField
              label={t("page.tickets.details.info.status")}
              contentWidth="narrow"
              isPending={changeStatusMutation.isPending}
            >
              <Select
                disabled={ticket.possibleStatuses.length === 0 || !ticket.canChangeStatus}
                selected={ticket.status}
                items={ticket.possibleStatuses}
                keySelector={(x) => x.id}
                renderOption={(x) => <TicketStatus description={x.name} labelColor={x.color} variant="dot" />}
                onChange={handleChangeStatus}
                data-testid="ticket-status-dropdown-container"
              />
            </DetailField>
            <DetailField
              label={t("page.tickets.details.info.assignee")}
              contentWidth="narrow"
              isPending={changeAssigneeMutation.isPending}
            >
              <Select
                placeholder={t("page.tickets.details.info.assignee.unassigned")}
                disabled={!ticket.canChangeAssignee}
                selected={displayedAssignee}
                items={ticket.possibleAssignees}
                keySelector={(x) => x.id}
                renderOption={(x) => x.fullName}
                onChange={initiateAssigneeChange}
              />
            </DetailField>
            {!!ticket.rating && (
              <DetailField label={t("page.tickets.details.info.rating")}>
                <RatingBadge rating={ticket.rating} size="lg" />
              </DetailField>
            )}
          </Inline>
          <TicketContent ticket={ticket} />
        </Stack>
      </SectionCard>

      <ConfirmModal
        tone="warning"
        title={t("page.tickets.before-assignee-change-modal.title")}
        description={t("page.tickets.before-assignee-change-modal.description")}
        isLoading={changeAssigneeMutation.isPending}
        isOpened={modalState.type === "confirmAssigneeWarning"}
        onReject={() => setModalState({ type: "none" })}
        onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
        onResolve={() => handleConfirmAssigneeWarning(modalState.assignee)}
      />

      <TicketLeaveCommentModal
        ticket={ticket}
        title={
          <Trans
            i18nKey="page.tickets.after-assignee-change-modal.title"
            values={{
              name: modalState.assignee?.fullName || "",
            }}
            components={{
              bold: <em />,
            }}
          />
        }
        description={
          <Trans
            i18nKey="page.tickets.after-assignee-change-modal.description"
            components={{
              yellow: (
                <Text variant="inherit" overflow="normal" tone="warning">
                  {null}
                </Text>
              ),
            }}
          />
        }
        canCommentInternal={ticket.canAddInternalNote}
        canCommentPublic={false}
        isOpened={modalState.type === "leaveAssigneeChangeComment"}
        onOpenChange={(isOpen) => !isOpen && handleAssigneeChangeWithOptionalNote(modalState.assignee)}
        onSubmit={(noteContent) => handleAssigneeChangeWithOptionalNote(modalState.assignee, noteContent)}
        assignee={modalState.assignee}
      />
      <TicketLeaveCommentModal
        ticket={ticket}
        title={t("page.tickets.after-status-change-modal.title")}
        description={
          <Trans
            i18nKey="page.tickets.after-status-change-modal.description"
            components={{
              yellow: (
                <Text variant="inherit" overflow="normal" tone="warning">
                  {null}
                </Text>
              ),
            }}
          />
        }
        canCommentInternal={ticket.canAddInternalNote}
        canCommentPublic={ticket.canAddPublicComment}
        isOpened={modalState.type === "leaveStatusChangeComment"}
        onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
        onSubmit={handleLeaveStatusChangeComment}
      />
      <TicketStatusChangeModal
        isOpened={modalState.type === "changeStatusWithOptions"}
        onOpenChange={(isOpen) => !isOpen && setModalState({ type: "none" })}
        onSubmit={handleSubmitStatusChangeWithOptions}
        targetStatus={possibleStatusesForSelect[0]}
        possibleStatuses={possibleStatusesForSelect}
      />
    </>
  );
}

interface TicketContentProps {
  ticket: AdminTicketDetailsDto | AppTicketDto;
}

export function TicketContent({ ticket }: TicketContentProps): React.ReactNode {
  const { t } = useTranslation();
  const [isTranslated, translationHandler] = useBool(false);

  const sessionUser = useSessionUser();
  const ticketQueries = useTicketQueries();
  const { data: translation, isLoading: isLoadingTranslation } = useQuery({
    ...ticketQueries.ticketTranslation({
      ticketId: ticket.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isTranslated,
  });

  const isTranslatable =
    !ticket.languageIsoCode ||
    (sessionUser.autoTranslationLanguage
      ? ticket.languageIsoCode !== sessionUser.autoTranslationLanguage.id
      : ticket.languageIsoCode !== sessionUser.language.id);
  const isTranslationNeeded = ticket.user.id !== sessionUser.id && isTranslatable;

  const title = isTranslated && translation ? translation.title : ticket.title;
  const content = isTranslated && translation ? translation.content : ticket.content;

  return (
    <Stack gap="sm">
      <DetailField label={t("page.tickets.details.info.title")} value={title || ""} />
      {content && (
        <DetailField label={t("page.tickets.details.info.description")}>
          <p data-testid="ticket-content">
            <MultiLineContent shouldHighlightLinks>{content}</MultiLineContent>
          </p>
        </DetailField>
      )}
      {isTranslationNeeded && (
        <Button
          variant="ghostPrimary"
          icon={<Globe02Icon />}
          isLoading={isLoadingTranslation}
          onClick={translationHandler.toggle}
        >
          {isTranslated
            ? t("page.tickets.details.activities.comment.translate.original")
            : t("page.tickets.details.activities.comment.translate")}
        </Button>
      )}
      {ticket.images && ticket.images.length > 0 && <Gallery images={ticket.images} />}
      {/* Currently, FE only supports 1 video */}
      {ticket.videos && ticket.videos.length > 0 && <Video video={ticket.videos[0]} shouldLoad />}
    </Stack>
  );
}
